import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Col, PopoverBody, Row } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { DropdownOption } from 'shared/prop-types/ReducerProps';
import { colors } from 'shared/defaultTheme';

import FilterCustomSelect from '../FilterCustomSelect';
import { TextField } from '../form/Inputs';
import { LabelError } from '../Labels';
import { Button } from '../Buttons';

import { PopoverWrapper } from './styles';

const FilterDropdownInput = ({
  className,
  trigger,
  placement,
  dropdownOptions,
  bothRequired,
  dropdownRequired,
  inputRequired,
  target,
  fieldSelectedOption,
  fieldSearchText,
  fieldSearchTextTwo,
  column,
  columnTwo,
  onSearch,
}) => {
  const { t } = useTranslation();
  const { clearList } = useSelector((state) => state.notification);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const {
    control,
    handleSubmit: handleSubmitFilter,
    formState: { errors: errors2 },
    reset,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const onSubmitFilter = (values) => {
    onSearch({
      ...values,
      fieldSelectedOption,
      fieldSearchText,
      fieldSearchTextTwo,
      column,
      columnTwo,
    });
    setPopoverOpen(!popoverOpen);
  };

  useEffect(() => {
    if (clearList) {
      reset({ selectedOption: '', searchText: '' });
    }
  }, [clearList]);

  const onClear = () => {
    reset({ selectedOption: '', searchText: '' });
    onSearch({ selectedOption: '', searchText: '', column, columnTwo });
  };

  return (
    <PopoverWrapper
      className={className}
      trigger={trigger}
      placement={placement}
      target={target}
      isOpen={popoverOpen}
      toggle={() => setPopoverOpen(!popoverOpen)}
    >
      <PopoverBody className="popover-body">
        <form id="filter-dropdown" className="form" onSubmit={handleSubmitFilter(onSubmitFilter)}>
          <div className="form__form-group">
            <Controller
              name="selectedOption"
              control={control}
              defaultValue=""
              rules={{
                required: {
                  value: bothRequired || dropdownRequired,
                  message: t('general_required_field'),
                },
              }}
              render={({ field }) => (
                <FilterCustomSelect
                  options={dropdownOptions}
                  defaultValue={field.value}
                  onChange={field.onChange}
                  backgroundColor={colors.transparent}
                />
              )}
            />
            {errors2.selectedOption && <LabelError label={errors2.selectedOption.message} />}
          </div>
          <div className="form__form-group">
            <Controller
              name="searchText"
              control={control}
              defaultValue=""
              rules={{
                required: {
                  value: bothRequired || inputRequired,
                  message: t('general_required_field'),
                },
              }}
              render={({ field }) => (
                <TextField
                  placeholder={t('general_search_placeholder')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors2.searchText !== undefined}
                />
              )}
            />
            {errors2.searchText && <LabelError label={errors2.searchText.message} />}
          </div>
          <Row className="w-100 remove-margin-horizontal">
            <Col sm={6} xs={6} className="remove-padding-horizontal">
              <Button
                className="text-uppercase"
                type="button"
                color="secondary"
                label={t('general_button_clean')}
                onClick={onClear}
              />
            </Col>
            <Col sm={6} xs={6} className="remove-padding-horizontal">
              <Button
                className="text-uppercase"
                type="submit"
                color="primary"
                label={t('general_button_filter')}
              />
            </Col>
          </Row>
        </form>
      </PopoverBody>
    </PopoverWrapper>
  );
};

FilterDropdownInput.defaultProps = {
  className: '',
  trigger: 'legacy',
  placement: 'bottom',
  dropdownOptions: [],
  bothRequired: false,
  dropdownRequired: false,
  inputRequired: false,
  columnTwo: '',
  fieldSearchTextTwo: '',
};

FilterDropdownInput.propTypes = {
  className: PropTypes.string,
  trigger: PropTypes.string,
  placement: PropTypes.string,
  dropdownOptions: PropTypes.arrayOf(DropdownOption),
  bothRequired: PropTypes.bool,
  dropdownRequired: PropTypes.bool,
  inputRequired: PropTypes.bool,
  target: PropTypes.string.isRequired,
  fieldSelectedOption: PropTypes.string.isRequired,
  fieldSearchText: PropTypes.string.isRequired,
  fieldSearchTextTwo: PropTypes.string,
  column: PropTypes.string.isRequired,
  columnTwo: PropTypes.string,
  onSearch: PropTypes.func.isRequired,
};

export default FilterDropdownInput;
